/**
 * AI CONTEXT: architecture - Giveaway state management with API integration
 * Journal: J-20250809-01, J-20250813-07, J-20250813-08
 * 
 * Purpose:
 * - Centralize giveaway data, credits, and entry logic
 * - Support optimistic UI with API fallback
 * - Provide mock mode when API is not configured
 * 
 * Future considerations:
 * - Migrate all mutations to secured REST API
 * - Add pagination/filtering for large datasets
 * - Implement server-driven subscriptions
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { authorizedFetch, isApiConfigured } from '../services/apiClient';
import { creditService } from '../services/creditService';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import { addHours, ensureDate, normalizeDateForVisibility, toIsoDateString } from '../utils/date';

const GiveawayContext = createContext();

export const useGiveaway = () => {
  const context = useContext(GiveawayContext);
  if (!context) {
    throw new Error('useGiveaway must be used within a GiveawayProvider');
  }
  return context;
};

// Mock giveaway data moved outside component
const mockGiveaways = [
  {
    id: '1',
    title: 'Tactical Gear Bundle',
    description: 'Complete tactical gear setup including vest, holster, and accessories',
    image: '/images/giveaway-1.jpg',
    tier: 'Bronze',
    value: 850,
    totalSlots: 100,
    availableSlots: 100,
    startDate: '2025-08-15',
    endDate: '2025-09-15',
    isActive: true,
    earlyAccessDate: '2025-08-13',
    category: 'Bronze',
    entries: []
  }
];

// No mock user credits. Credits are sourced exclusively from the API.

export const GiveawayProvider = ({ children }) => {
  const [giveaways, setGiveaways] = useState([]);
  const [userCredits, setUserCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  const { isAuthenticated, user, authChecked } = useAuth();
  const subscriptionContext = useSubscription();
  const subscriptionIsUserSubscribed = subscriptionContext?.isUserSubscribed;
  
  // Check if user is subscribed (for premium features)
  // Prefer email over id since subscriptions are keyed by email in the backend
  const isUserSubscribed = (userId) => {
    let derivedSubscriptionStatus;

    if (typeof subscriptionIsUserSubscribed === 'function') {
      try {
        // Use email first, then fall back to id, then userId parameter
        const identifier = user?.email || user?.id || userId;
        derivedSubscriptionStatus = subscriptionIsUserSubscribed(identifier);
      } catch (subscriptionError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 GiveawayContext: SubscriptionContext.isUserSubscribed threw an error:', subscriptionError);
        }
      }

      if (typeof derivedSubscriptionStatus === 'boolean') {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 GiveawayContext: isUserSubscribed (subscription context)', {
            userId,
            identifier: user?.email || user?.id || userId,
            userEmail: user?.email,
            userIsSubscriber: user?.isSubscriber,
            derivedSubscriptionStatus
          });
        }
        return derivedSubscriptionStatus;
      }
    }

    const fallbackSubscribed = Boolean(user?.isSubscriber);
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 GiveawayContext: isUserSubscribed fallback check:', {
        userId,
        userEmail: user?.email,
        isSubscriberAttribute: user?.isSubscriber,
        result: fallbackSubscribed
      });
    }
    return fallbackSubscribed;
  };

  const fetchGiveaways = useCallback(async () => {
    try {
      setLoading(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Debug: fetchGiveaways called, user.isAdmin:', user?.isAdmin);
      }
      
      if (isApiConfigured()) {
        const data = await authorizedFetch('/giveaways', { method: 'GET' });
        let base = Array.isArray(data) ? data : [];
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Debug: Base giveaways from API:', base.length);
        }
        
        // For admin users, fetch all entries for each giveaway
        if (user?.isAdmin) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Debug: Admin user detected, fetching entries for each giveaway...');
          }
          const giveawaysWithEntries = await Promise.all(
            base.map(async (giveaway) => {
              try {
                const entriesResponse = await authorizedFetch(`/giveaways/${giveaway.id}/entries`, { method: 'GET' });
                if (entriesResponse && Array.isArray(entriesResponse.entries)) {
                  return {
                    ...giveaway,
                    entries: entriesResponse.entries.map(entry => ({
                      userId: entry.email, // Use email as userId for display
                      slotNumber: entry.slotNumber,
                      date: entry.date,
                      email: entry.email,
                      tier: entry.tier
                    }))
                  };
                }
              } catch (error) {
                console.warn(`Failed to fetch entries for giveaway ${giveaway.id}:`, error);
              }
              return { ...giveaway, entries: [] };
            })
          );
          base = giveawaysWithEntries;
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Debug: Final giveaways with entries:', base);
          }
        } else {
          // For regular users, overlay local user entries (MVP persistence) so entries survive reloads
          if (user?.email) {
            const localEntries = JSON.parse(localStorage.getItem(`tolbert_user_entries_${user.email}`) || '[]');
            if (Array.isArray(localEntries) && localEntries.length > 0) {
              base = base.map(g => {
                const toAdd = localEntries
                  .filter(e => e.giveawayId === g.id)
                  .map(e => ({ userId: user.email, slotNumber: e.slotNumber, date: e.date, isAutoEntry: !!e.isAutoEntry }));
                if (toAdd.length === 0) return { ...g, entries: g.entries || [] };
                const newEntries = [...(g.entries || []), ...toAdd];
                return { ...g, entries: newEntries, availableSlots: Math.max(0, (g.availableSlots || 0) - toAdd.length) };
              });
            }
          }
        }
        
        // Ensure only giveaways appear (defense-in-depth) and normalize images
        const normalized = base
          .filter(g => g && g.id && g.title !== undefined)
          .map(g => ({
            ...g,
            images: Array.isArray(g.images) ? g.images : (g.image ? [g.image] : []),
            image: g.image || (Array.isArray(g.images) && g.images.length > 0 ? g.images[0] : '/files/Logo.png')
          }));
        setGiveaways(normalized);
      } else {
        let base = mockGiveaways;
        
        if (user?.email) {
          const localEntries = JSON.parse(localStorage.getItem(`tolbert_user_entries_${user.email}`) || '[]');
          if (Array.isArray(localEntries) && localEntries.length > 0) {
            base = base.map(g => {
              const toAdd = localEntries
                .filter(e => e.giveawayId === g.id)
                .map(e => ({ userId: user.email, slotNumber: e.slotNumber, date: e.date, isAutoEntry: !!e.isAutoEntry }));
              if (toAdd.length === 0) return { ...g };
              return { ...g, entries: [...g.entries, ...toAdd], availableSlots: Math.max(0, g.availableSlots - toAdd.length) };
            });
          }
        }
        setGiveaways(base.map(g => ({
          ...g,
          images: Array.isArray(g.images) ? g.images : (g.image ? [g.image] : []),
          image: g.image || (Array.isArray(g.images) && g.images.length > 0 ? g.images[0] : '/files/Logo.png')
        })));
      }
    } catch (e) {
      console.error('Failed to load giveaways from API:', e);
      let base = mockGiveaways;
      
      if (user?.email) {
        const localEntries = JSON.parse(localStorage.getItem(`tolbert_user_entries_${user.email}`) || '[]');
        if (Array.isArray(localEntries) && localEntries.length > 0) {
          base = base.map(g => {
            const toAdd = localEntries
              .filter(e => e.giveawayId === g.id)
              .map(e => ({ userId: user.email, slotNumber: e.slotNumber, date: e.date, isAutoEntry: !!e.isAutoEntry }));
            if (toAdd.length === 0) return { ...g };
            return { ...g, entries: [...g.entries, ...toAdd], availableSlots: Math.max(0, g.availableSlots - toAdd.length) };
          });
        }
      }
      setGiveaways(base.map(g => ({
        ...g,
        images: Array.isArray(g.images) ? g.images : (g.image ? [g.image] : []),
        image: g.image || (Array.isArray(g.images) && g.images.length > 0 ? g.images[0] : '/files/Logo.png')
      })));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load authenticated user's credits from API (no mock fallback)
  const fetchUserCredits = useCallback(async () => {
    try {
      console.log('🔍 GiveawayContext: fetchUserCredits called with:', {
        isApiConfigured: isApiConfigured(),
        isAuthenticated,
        userEmail: user?.email,
        userId: user?.id,
        user: user
      });
      
      if (isApiConfigured() && isAuthenticated && user?.email) {
        console.log('🔍 GiveawayContext: Conditions met, calling creditService.getMyCredits()');
        let apiCredits = null;
        try {
          apiCredits = await creditService.getMyCredits();
          console.log('🔍 GiveawayContext: API credits response:', apiCredits);
        } catch (e) {
          console.warn('🔍 GiveawayContext: First attempt failed, retrying after 200ms delay:', e);
          // Brief backoff and one retry to mitigate Safari token readiness race
          await new Promise(r => setTimeout(r, 200));
          apiCredits = await creditService.getMyCredits();
          console.log('🔍 GiveawayContext: Retry API credits response:', apiCredits);
        }
        if (apiCredits && typeof apiCredits === 'object') {
          console.log('🔍 GiveawayContext: Setting user credits from API:', apiCredits);
          setUserCredits([
            {
              userId: user.email, // Use email as userId for consistency with API
              bronzeCredits: apiCredits.bronzeCredits || 0,
              silverCredits: apiCredits.silverCredits || 0,
              goldCredits: apiCredits.goldCredits || 0,
              platinumCredits: apiCredits.platinumCredits || 0,
              autoEntryEnabled: false,
              lastAutoEntryDate: null,
              monthlyCreditIssued: null,
              autoEntryOptOut: true, // Disabled by default
            },
          ]);
          return;
        } else {
          console.warn('🔍 GiveawayContext: API credits response invalid:', apiCredits);
        }
      } else {
        console.log('🔍 GiveawayContext: Conditions not met for API call:', {
          isApiConfigured: isApiConfigured(),
          isAuthenticated,
          hasUserEmail: !!user?.email
        });
      }
      // If API not configured or no data, clear to empty; getters default to zeros
      console.log('🔍 GiveawayContext: Clearing user credits to empty array');
      setUserCredits([]);
    } catch (err) {
      console.warn('🔍 GiveawayContext: Failed to fetch user credits; leaving empty/zero', err);
      setUserCredits([]);
    }
  }, [isAuthenticated, user]);

  const refreshUserCredits = useCallback(async () => {
    await fetchUserCredits();
  }, [fetchUserCredits]);

  // Initial load
  useEffect(() => {
    // Wait until auth state has been checked to avoid early 401s on Safari
    if (!authChecked) return;
    
    // Only fetch giveaways initially (public data)
    fetchGiveaways();
    
    // Only fetch user credits if user is authenticated and has email
    if (isAuthenticated && user?.email) {
      fetchUserCredits();
    }
  }, [authChecked, isAuthenticated, user, fetchGiveaways, fetchUserCredits]);

  // Re-fetch after authentication/user change
  useEffect(() => {
    if (isApiConfigured() && isAuthenticated && user?.email) {
      // Safari-specific delay to allow token generation to complete
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isSafari) {
        const delay = isMobile ? 2000 : 1000; // Mobile Safari needs longer delay
        if (process.env.NODE_ENV !== 'production') {
          console.log(`🔍 GiveawayContext: Safari detected, delaying API calls by ${delay}ms to allow token generation`);
        }
        
        const timer = setTimeout(() => {
          fetchGiveaways();
          fetchUserCredits();
        }, delay);
        
        return () => clearTimeout(timer);
      } else {
        // Non-Safari browsers can proceed immediately
        fetchGiveaways();
        fetchUserCredits();
      }
    }
  }, [isAuthenticated, user, fetchGiveaways, fetchUserCredits]);

  // AI CONTEXT: data-cleanup - Automatic localStorage cleanup for stale entries
  // Reasoning: Clean up stale localStorage entries when giveaways are loaded to prevent incorrect active entry counts
  // Constraints: Must preserve valid entries and only remove expired or invalid ones
  // Future: Consider implementing server-side entry validation to avoid client-side cleanup
  useEffect(() => {
    if (!user?.email || giveaways.length === 0) return;
    
    // Clean up stale localStorage entries that might cause incorrect active entry counts
    const cleanupStaleEntries = () => {
      try {
        const localEntries = JSON.parse(localStorage.getItem(`tolbert_user_entries_${user.email}`) || '[]');
        if (Array.isArray(localEntries) && localEntries.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 GiveawayContext: Checking for stale localStorage entries:', localEntries);
          }
          
          // Filter out entries for giveaways that no longer exist or are expired
          const currentGiveawayIds = new Set(giveaways.map(g => g.id));
          const now = new Date();
          
          const validEntries = localEntries.filter(entry => {
            // Remove entries for giveaways that don't exist anymore
            if (!currentGiveawayIds.has(entry.giveawayId)) {
              if (process.env.NODE_ENV === 'development') {
                console.log(`🔍 GiveawayContext: Removing entry for non-existent giveaway: ${entry.giveawayId}`);
              }
              return false;
            }
            
            // Remove entries for expired giveaways
            const giveaway = giveaways.find(g => g.id === entry.giveawayId);
            if (giveaway && giveaway.endDate) {
              const endDate = new Date(giveaway.endDate);
              if (!Number.isNaN(endDate.getTime()) && endDate < now) {
                if (process.env.NODE_ENV === 'development') {
                  console.log(`🔍 GiveawayContext: Removing entry for expired giveaway: ${entry.giveawayId} (ended: ${giveaway.endDate})`);
                }
                return false;
              }
            }
            
            return true;
          });
          
          // Update localStorage with only valid entries
          if (validEntries.length !== localEntries.length) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`🔍 GiveawayContext: Cleaned up ${localEntries.length - validEntries.length} stale entries`);
            }
            if (validEntries.length === 0) {
              localStorage.removeItem(`tolbert_user_entries_${user.email}`);
            } else {
              localStorage.setItem(`tolbert_user_entries_${user.email}`, JSON.stringify(validEntries));
            }
          }
        }
      } catch (error) {
        console.warn('GiveawayContext: Failed to cleanup stale localStorage entries:', error);
      }
    };
    
    cleanupStaleEntries();
  }, [user?.email, giveaways]);

  // Get user credits with enhanced tracking
  const getUserCredits = (userId) => {
    // userId can be either email or id, try both for backward compatibility
    const credits = userCredits.find(credits => 
      credits.userId === userId || 
      (user && user.email === userId) ||
      (user && user.id === userId)
    );
    
    if (credits) {
      return credits;
    }
    
    // Return default credits if none found
    return {
      userId: userId,
      bronzeCredits: 0,
      silverCredits: 0,
      goldCredits: 0,
      platinumCredits: 0,
      autoEntryEnabled: false,
      lastAutoEntryDate: null,
      monthlyCreditIssued: null,
      autoEntryOptOut: true // Disabled by default
    };
  };

  // Add credits when user purchases products
  const addCredits = (userId, tier, amount = 1) => {
    setUserCredits(prev => {
      const existing = prev.find(credits => credits.userId === userId);
      const tierKey = `${String(tier || 'Bronze').toLowerCase()}Credits`;
      if (existing) {
        return prev.map(credits => 
          credits.userId === userId 
            ? { ...credits, [tierKey]: (credits[tierKey] || 0) + amount }
            : credits
        );
      } else {
        return [...prev, {
          userId,
          bronzeCredits: tier === 'Bronze' ? amount : 0,
          silverCredits: tier === 'Silver' ? amount : 0,
          goldCredits: tier === 'Gold' ? amount : 0,
          platinumCredits: tier === 'Platinum' ? amount : 0,
          autoEntryEnabled: false,
          lastAutoEntryDate: null,
          monthlyCreditIssued: null,
          autoEntryOptOut: true // Disabled by default
        }];
      }
    });
  };

  // Issue monthly credit for subscribers
  const issueMonthlyCredit = (userId) => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    setUserCredits(prev => 
      prev.map(credits => {
        if (credits.userId === userId) {
          const lastIssued = credits.monthlyCreditIssued;
          const lastMonth = lastIssued ? lastIssued.substring(0, 7) : null;
          
          // Only issue if not already issued this month
          if (lastMonth !== currentMonth) {
            return {
              ...credits,
              bronzeCredits: credits.bronzeCredits + 1,
              monthlyCreditIssued: now.toISOString().split('T')[0]
            };
          }
        }
        return credits;
      })
    );
  };

  // Use credits for giveaway entry
  const useCredit = (userId, tier) => {
    const credits = getUserCredits(userId);
    const creditKey = `${String(tier || 'Bronze').toLowerCase()}Credits`;
    
    if (credits[creditKey] > 0) {
      setUserCredits(prev => 
        prev.map(userCredits => 
          userCredits.userId === userId 
            ? { ...userCredits, [creditKey]: userCredits[creditKey] - 1 }
            : userCredits
        )
      );
      return true;
    }
    return false;
  };

  // Auto-entry feature disabled - always return false
  const processAutoEntry = (giveaway, user) => {
    // Feature disabled per client request - auto-entry is not available
    return false;
  };

  const enterGiveaway = async (giveawayId, slotNumbers, userId, isAutoEntry = false) => {
    const giveaway = giveaways.find(g => String(g.id) === String(giveawayId));
    if (!giveaway) return false;

    // Check if user has credits for this tier (unless it's auto-entry)
    if (!isAutoEntry) {
      const credits = getUserCredits(userId);
      const creditKey = `${String(giveaway?.tier || 'Bronze').toLowerCase()}Credits`;
      if (credits[creditKey] <= 0) {
        toast.error(`You need a ${giveaway.tier} credit to enter this giveaway. Purchase a ${giveaway.tier} product to earn credits.`);
        return false;
      }
    }

    // Server-side entry creation (atomic with credits/slots when API configured)
    if (isApiConfigured()) {
      try {
        await authorizedFetch(`/giveaways/${giveawayId}/entries`, {
          method: 'POST',
          body: JSON.stringify({ slots: slotNumbers })
        });
        // Refresh credits and giveaways after transactional entry
        await fetchUserCredits();
        await fetchGiveaways();
      } catch (e) {
        toast.error(e?.message || 'Failed to enter giveaway');
        return false;
      }
    } else {
      // Local validation and UI-only update if API not configured
      const availableSlots = getAvailableSlots(giveawayId);
      const invalidSlots = slotNumbers.filter(slot => !availableSlots.includes(slot));
      if (invalidSlots.length > 0) {
        toast.error(`Slot(s) ${invalidSlots.join(', ')} are not available`);
        return false;
      }
      const entryDate = new Date().toISOString().split('T')[0];
      setGiveaways(prevGiveaways =>
        prevGiveaways.map(g => {
          if (g.id === giveawayId) {
            const newEntries = slotNumbers.map(slotNumber => ({
              userId,
              slotNumber,
              date: entryDate,
              isAutoEntry
            }));
            return {
              ...g,
              entries: [...g.entries, ...newEntries],
              availableSlots: g.availableSlots - slotNumbers.length
            };
          }
          return g;
        })
      );
      try {
        const key = `tolbert_user_entries_${userId}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const toStore = [
          ...existing,
          ...slotNumbers.map(slotNumber => ({ giveawayId, slotNumber, date: entryDate, isAutoEntry }))
        ];
        localStorage.setItem(key, JSON.stringify(toStore));
      } catch (_err) {}
    }
    
    const entryType = isAutoEntry ? 'auto-entry' : 'manual entry';
    toast.success(`${entryType} successful! Entered ${slotNumbers.length} slot(s) in giveaway.`);
    return true;
  };

  const getUserEntries = (userId) => {
    return giveaways.flatMap(giveaway =>
      giveaway.entries
        .filter(entry => entry.userId === userId)
        .map(entry => ({
          ...entry,
          giveawayId: giveaway.id,
          giveawayTitle: giveaway.title,
          giveawayTier: giveaway.tier
        }))
    );
  };

  const getAvailableSlots = (giveawayId) => {
    const giveaway = giveaways.find(g => g.id === giveawayId);
    if (!giveaway) return [];
    
    const takenSlots = giveaway.entries.map(entry => entry.slotNumber);
    const allSlots = Array.from({ length: giveaway.totalSlots }, (_, i) => i + 1);
    
    return allSlots.filter(slot => !takenSlots.includes(slot));
  };

  // Enhanced slot swap with validation
  const swapSlot = (giveawayId, userId, currentSlot, newSlot) => {
    const giveaway = giveaways.find(g => g.id === giveawayId);
    if (!giveaway) return false;

    // Check if new slot is available
    const availableSlots = getAvailableSlots(giveawayId);
    if (!availableSlots.includes(newSlot)) {
      toast.error('Selected slot is not available');
      return false;
    }

    // Check if user has an entry in current slot
    const userEntry = giveaway.entries.find(entry => 
      entry.userId === userId && entry.slotNumber === currentSlot
    );
    
    if (!userEntry) {
      toast.error('You do not have an entry in that slot');
      return false;
    }

    setGiveaways(prevGiveaways =>
      prevGiveaways.map(g => {
        if (g.id === giveawayId) {
          return {
            ...g,
            entries: g.entries.map(entry => 
              entry.userId === userId && entry.slotNumber === currentSlot
                ? { ...entry, slotNumber: newSlot }
                : entry
            )
          };
        }
        return g;
      })
    );

    // Update local storage mirror
    try {
      const key = `tolbert_user_entries_${userId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = existing.map(e => (
        e.giveawayId === giveawayId && e.slotNumber === currentSlot ? { ...e, slotNumber: newSlot } : e
      ));
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (_err) {
      // ignore storage errors
    }

    toast.success(`Slot swapped from ${currentSlot} to ${newSlot}`);
    return true;
  };

  // Enhanced auto-entry toggle with credit management
  const toggleAutoEntry = (userId, enabled) => {
    setUserCredits(prev => 
      prev.map(credits => 
        credits.userId === userId 
          ? { ...credits, autoEntryOptOut: !enabled }
          : credits
      )
    );
    
    toast.success(`Auto-entry ${enabled ? 'enabled' : 'disabled'}`);
  };

  // Check if user can see giveaway (early access for subscribers)
  const canViewGiveaway = (giveaway, user) => {
    if (!user) return false;
    
    // AI CONTEXT: business-logic - Admin override for giveaway visibility
    // Reasoning: Admins should be able to see ALL giveaways regardless of status
    // Constraints: Only users with isAdmin flag can bypass visibility rules
    // Future: Consider admin-specific UI indicators for hidden giveaways
    if (user.isAdmin) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 GiveawayContext: Admin user - bypassing visibility checks for:', giveaway.title);
      }
      return true; // Admins can see all giveaways
    }
    
    const now = new Date();
    // Use ensureDate for calculations to preserve time precision, then normalize for comparison
    const startDateForCalc = ensureDate(giveaway.startDate);
    const startDate = normalizeDateForVisibility(giveaway.startDate);
    
    // Calculate early access date - use provided date or calculate 48 hours before start
    // Use ensureDate (not normalized) for time-sensitive calculation
    let earlyAccessDate = giveaway.earlyAccessDate ? normalizeDateForVisibility(giveaway.earlyAccessDate) : null;
    if (!earlyAccessDate && startDateForCalc) {
      earlyAccessDate = addHours(startDateForCalc, -48);
    }
    
    // AI CONTEXT: business-logic - Subscription-only visibility
    // Reasoning: Allow admins to create subscription-exclusive giveaways
    // Constraints: Non-subscribers cannot see these giveaways at all
    // Future: Consider tiered subscription levels
    
    // Check actual subscription status from subscription context
    // Use email instead of id since subscriptions are keyed by email in the backend
    const userIsSubscribed = isUserSubscribed(user.email || user.id);

    // AI CONTEXT: bug-fix - If startDate is missing, default to visible for non-subscription-only giveaways
    // Reasoning: Admins can create "active now" giveaways without a startDate; non-admins should still see them
    // Journal: J-20250103-04
    if (!giveaway.subscriptionVisibilityOnly && !startDate) {
      return true;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 GiveawayContext: canViewGiveaway check:', {
        giveawayTitle: giveaway.title,
        currentDate: now.toISOString(),
        currentDateSimple: now.toISOString().split('T')[0],
        startDate: giveaway.startDate,
        startDateNormalized: startDate ? startDate.toISOString() : null,
        earlyAccessDate: earlyAccessDate ? earlyAccessDate.toISOString() : null,
        earlyAccessDateSimple: toIsoDateString(earlyAccessDate),
        userIsSubscribed,
        subscriptionOnly: giveaway.subscriptionVisibilityOnly,
        isAdmin: user.isAdmin
      });
    }
    
    // Normalize dates to start of day for proper date comparison (handles timezone issues)
    const normalizeToStartOfDay = (date) => {
      if (!date) return null;
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const nowStartOfDay = normalizeToStartOfDay(now);
    
    // If giveaway is marked subscriptionVisibilityOnly, treat it as **subscriber early access**
    // (subscribers can see it from earlyAccessDate; non-subscribers can see it from startDate).
    //
    // IMPORTANT: This matches AccessControl UI logic and prevents a situation where admins
    // can see a giveaway (admin bypass) but normal users can *never* see it, even after startDate.
    if (giveaway.subscriptionVisibilityOnly) {
      if (userIsSubscribed) {
        const subscriberUnlock = earlyAccessDate || startDate;
        const unlockStartOfDay = normalizeToStartOfDay(subscriberUnlock);
        return unlockStartOfDay ? nowStartOfDay >= unlockStartOfDay : false;
      }
      // Non-subscribers unlock at the public start date (not early access).
      const publicUnlockStartOfDay = normalizeToStartOfDay(startDate);
      return publicUnlockStartOfDay ? nowStartOfDay >= publicUnlockStartOfDay : false;
    }
    
    // Regular giveaway visibility logic
    const subscriberUnlock = earlyAccessDate || startDate;
    const freeUnlock = startDate;
    const subscriberUnlockStartOfDay = normalizeToStartOfDay(subscriberUnlock);
    const freeUnlockStartOfDay = normalizeToStartOfDay(freeUnlock);
    const canView = userIsSubscribed
      ? (subscriberUnlockStartOfDay ? nowStartOfDay >= subscriberUnlockStartOfDay : false)
      : (freeUnlockStartOfDay ? nowStartOfDay >= freeUnlockStartOfDay : false);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 GiveawayContext: canView calculation:', {
        subscriberUnlock: subscriberUnlock ? subscriberUnlock.toISOString() : null,
        freeUnlock: freeUnlock ? freeUnlock.toISOString() : null,
        comparison: userIsSubscribed 
          ? `now (${now.toISOString()}) >= subscriberUnlock (${subscriberUnlock ? subscriberUnlock.toISOString() : 'null'})`
          : `now (${now.toISOString()}) >= freeUnlock (${freeUnlock ? freeUnlock.toISOString() : 'null'})`,
        canView
      });
    }
    
    return canView;
  };

  const canEnterGiveaway = (giveaway, user) => {
    if (!user) return false;
    
    // Check if giveaway is completed or has a winner - no more entries allowed
    if (giveaway?.status === 'completed' || giveaway?.winner) {
      return false;
    }
    
    // Check if user can view the giveaway
    if (!canViewGiveaway(giveaway, user)) {
      return false;
    }
    
    // AI CONTEXT: business-logic - Check per-giveaway entry limit instead of global limit
    const giveawayEntryLimit = giveaway.entryLimit || 5; // Default to 5 if not set
    const userEntriesForGiveaway = giveaway.entries.filter(entry => entry.userId === user.email).length;
    if (userEntriesForGiveaway >= giveawayEntryLimit) return false;
    
    return true;
  };

  const selectWinner = (giveawayId, method = 'random') => {
    const giveaway = giveaways.find(g => g.id === giveawayId);
    if (!giveaway || giveaway.entries.length === 0) {
      toast.error('No entries found for this giveaway');
      return null;
    }
    
    let winner;
    if (method === 'random') {
      const randomIndex = Math.floor(Math.random() * giveaway.entries.length);
      winner = giveaway.entries[randomIndex];
    } else {
      // Manual selection - just pick the first entry for demo
      winner = giveaway.entries[0];
    }
    
    toast.success(`Winner selected: Slot ${winner.slotNumber}`);
    return winner;
  };

  const createGiveaway = async (giveawayData) => {
    try {
      const normalized = { ...giveawayData };
      if (!normalized.image && Array.isArray(normalized.images) && normalized.images.length > 0) {
        normalized.image = normalized.images[0];
      }
      if (!normalized.image) {
        normalized.image = '/files/Logo.png';
      }
      if (isApiConfigured()) {
        await authorizedFetch('/giveaways', {
          method: 'POST',
          body: JSON.stringify(normalized)
        });
        // Re-fetch from API to ensure source-of-truth matches DB (avoids drift)
        await fetchGiveaways();
        // Force refresh of components that might be cached
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 GiveawayContext: Dispatching giveawayDataUpdated event after createGiveaway');
        }
        window.dispatchEvent(new CustomEvent('giveawayDataUpdated'));
      } else {
        const newGiveaway = {
          id: Date.now().toString(),
          ...normalized,
          entries: [],
          isActive: true
        };
        setGiveaways(prev => [...prev, newGiveaway]);
        // Also dispatch event for mock mode to ensure Home page updates
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 GiveawayContext: Dispatching giveawayDataUpdated event after createGiveaway (mock mode)');
        }
        window.dispatchEvent(new CustomEvent('giveawayDataUpdated'));
      }
      toast.success('Giveaway created successfully!');
      return true;
    } catch (e) {
      console.error('Create giveaway failed:', e);
      toast.error('Failed to create giveaway');
      return false;
    }
  };

  const updateGiveaway = async (giveawayId, updates) => {
    try {
      const normalized = { ...updates };
      if (!normalized.image && Array.isArray(normalized.images) && normalized.images.length > 0) {
        normalized.image = normalized.images[0];
      }
      if (!normalized.image && (!Array.isArray(normalized.images) || normalized.images.length === 0)) {
        normalized.image = '/files/Logo.png';
      }
      if (isApiConfigured()) {
        await authorizedFetch(`/giveaways/${giveawayId}`, {
          method: 'PATCH',
          body: JSON.stringify(normalized)
        });
        // Reload to reflect server mutational side-effects and normalization
        await fetchGiveaways();
        // Force refresh of components that might be cached
        window.dispatchEvent(new CustomEvent('giveawayDataUpdated'));
      } else {
        setGiveaways(prev => prev.map(g => (g.id === giveawayId ? { ...g, ...normalized } : g)));
      }
      toast.success('Giveaway updated successfully!');
      return true;
    } catch (e) {
      console.error('Update giveaway failed:', e);
      toast.error('Failed to update giveaway');
      return false;
    }
  };

  const deleteGiveaway = async (giveawayId) => {
    try {
      if (isApiConfigured()) {
        await authorizedFetch(`/giveaways/${giveawayId}`, { method: 'DELETE' });
        // Re-fetch to ensure deletion is reflected from server
        await fetchGiveaways();
        // Force refresh of components that might be cached
        window.dispatchEvent(new CustomEvent('giveawayDataUpdated'));
      } else {
        setGiveaways(prev => prev.filter(g => g.id !== giveawayId));
      }
      toast.success('Giveaway deleted successfully!');
      return true;
    } catch (e) {
      console.error('Delete giveaway failed:', e);
      toast.error('Failed to delete giveaway');
      return false;
    }
  };

  const value = {
    giveaways,
    loading,
    fetchGiveaways,
    enterGiveaway,
    getUserEntries,
    getAvailableSlots,
    canEnterGiveaway,
    canViewGiveaway,
    selectWinner,
    createGiveaway,
    updateGiveaway,
    deleteGiveaway,
    getUserCredits,
    addCredits,
    useCredit,
    swapSlot,
    toggleAutoEntry,
    processAutoEntry,
    issueMonthlyCredit,
    refreshUserCredits
  };

  return (
    <GiveawayContext.Provider value={value}>
      {children}
    </GiveawayContext.Provider>
  );
}; 